import { Box, Flex, Heading, Text, Wrap } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import * as m from 'motion/react-m';

import { Link } from '#features/routing';
import { Image } from '#features/ui';

type GameItem = {
  __typename?: string;
  id?: string;
  name?: string;
  slug?: string | null;
  icon?: string;
};

type AllGamesProps = {
  games: GameItem[];
  currentSlug: string;
};

export const AllGames = ({ games, currentSlug }: AllGamesProps) => {
  const { t } = useLingui();

  const filteredGames = games
    .filter((game) => game.__typename === 'Game' && game.slug && game.slug !== currentSlug)
    .toSorted((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

  if (filteredGames.length === 0) {
    return null;
  }

  return (
    <Box as="section" aria-label="All Games" w="100%">
      <Heading
        size={{ base: 'lg', md: 'xl' }}
        w="100%"
        textAlign="center"
        fontWeight="700"
        mb={6}
        as="h3"
        color="fg.emphasized"
      >
        {t`v2.allGames.title`}
      </Heading>
      <Wrap gap={2} justify="center">
        {filteredGames.map((game, index) => (
          <Box key={game.id} asChild>
            <m.div
              initial={{ opacity: 0, scale: 0.92 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.025, duration: 0.3 }}
            >
              {/* Real link to the game landing - distributes crawl/authority to every
                  game page (the site's highest-intent SEO surface). The title adds a
                  keyword-rich anchor context ("<Game> LFG - find teammates"). */}
              {/* @ts-expect-error dynamic route param */}
              <Link
                to="/{-$locale}/$gameSlug"
                params={{ gameSlug: game.slug ?? '' }}
                title={game.name ? `${game.name} LFG - find teammates` : undefined}
              >
                <Flex
                  alignItems="center"
                  gap={2}
                  px={3}
                  py={1.5}
                  bg="whiteAlpha.50"
                  border="1px solid"
                  borderColor="whiteAlpha.100"
                  borderRadius="full"
                  transition="border-color 0.2s, background-color 0.2s"
                  _hover={{ borderColor: 'purple.400', bg: 'whiteAlpha.100' }}
                >
                  {game.icon && (
                    <Box w="22px" h="22px" flexShrink={0} borderRadius="md" overflow="hidden">
                      <Image
                        src={game.icon}
                        alt={game.name ?? ''}
                        width={44}
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    </Box>
                  )}
                  <Text fontSize="sm" fontWeight="600" whiteSpace="nowrap" color="fg.emphasized">
                    {game.name}
                  </Text>
                </Flex>
              </Link>
            </m.div>
          </Box>
        ))}
      </Wrap>
    </Box>
  );
};
